import {
  createContext,
  useCallback,
  useContext,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from 'react';
import { useQueryClient, type QueryKey } from '@tanstack/react-query';

/**
 * Work that outlives the dialog that started it.
 *
 * A chassis write is not a form submission. A resync opens an SSH session and
 * re-states a subscriber's config; an inventory sweep walks a whole shelf.
 * Holding a modal open with a spinner for that pins the operator to one screen
 * for something they have already decided — and worse, it makes the outcome
 * hostage to the dialog: navigate away, or close the tab, and the answer is
 * gone even though the platform carried on doing the work.
 *
 * So the dialog CLOSES on confirm and the promise moves here, to a provider
 * mounted at the root that outlives every page. Two surfaces read from it: a
 * toast that appears when a task settles, and the tasks tray in the header
 * that says what is running right now.
 *
 * **History and toasts are separate.** A toast is transient — a success one
 * dismisses itself, because a stack of receipts trains people to swipe
 * everything away, while a FAILURE stays until it is dismissed, since the one
 * message that must not scroll past unread is a chassis refusing a write.
 * Neither leaves the tray: dismissing the bubble is saying "I saw it", not
 * "that never happened".
 *
 * Nothing is persisted. A reload empties the tray, which is honest — this is
 * what THIS tab started, and the durable record of what happened to a chassis
 * is the audit trail and the ONU's own page, not a bubble in the corner.
 */

export interface TaskRecord {
  id: string;
  /** What was started, in the operator's words: "Resync RTEGC6A2955E". */
  label: string;
  state: 'running' | 'ok' | 'failed';
  /** The outcome, once there is one. */
  message: string | null;
  /** Where to go to see it properly. */
  href: string | null;
  startedAt: number;
  finishedAt: number | null;
  /** Whether the transient bubble is still showing for this one. */
  toast: boolean;
}

export interface StartTask<T> {
  /** What the operator asked for, named so a toast makes sense out of context. */
  label: string;
  /** The work. Started immediately; its promise is owned by this provider. */
  run: () => Promise<T>;
  /**
   * What to say when it worked. Given the result, because the useful sentence
   * is usually in it ("re-stated 12 lines", "8 of 8 answered").
   */
  done?: (result: T) => string;
  /** Where the toast and the tray row should link. */
  href?: string;
  /** Query keys to refresh once it lands, so open pages stop showing stale rows. */
  invalidate?: QueryKey[];
}

interface TasksApi {
  /** Start work and stop waiting for it. Returns immediately. */
  start<T>(task: StartTask<T>): void;
  /** Everything this tab has started, newest last. Capped. */
  tasks: TaskRecord[];
  running: number;
  /** Hide one bubble. The task stays in the tray. */
  dismissToast(id: string): void;
  /** Empty the tray of everything that has finished. */
  clearFinished(): void;
}

const TasksContext = createContext<TasksApi | null>(null);

/** How long a success bubble stays up. Long enough to read, short enough to ignore. */
const SUCCESS_MS = 9000;
/** How many finished tasks the tray remembers before dropping the oldest. */
const HISTORY = 20;

export function TasksProvider({ children }: { children: ReactNode }) {
  const queryClient = useQueryClient();
  const [tasks, setTasks] = useState<TaskRecord[]>([]);
  const seq = useRef(0);

  const dismissToast = useCallback((id: string) => {
    setTasks((list) => list.map((t) => (t.id === id ? { ...t, toast: false } : t)));
  }, []);

  const clearFinished = useCallback(() => {
    setTasks((list) => list.filter((t) => t.state === 'running'));
  }, []);

  const start = useCallback(
    <T,>(task: StartTask<T>) => {
      seq.current += 1;
      const id = `task-${seq.current}-${Date.now()}`;
      setTasks((list) =>
        [
          ...list,
          {
            id,
            label: task.label,
            state: 'running' as const,
            message: null,
            href: task.href ?? null,
            startedAt: Date.now(),
            finishedAt: null,
            toast: true,
          },
        ].slice(-HISTORY),
      );

      const settle = (state: 'ok' | 'failed', message: string) => {
        setTasks((list) =>
          list.map((t) =>
            t.id === id ? { ...t, state, message, finishedAt: Date.now() } : t,
          ),
        );
        // Successes clear themselves; failures wait to be acknowledged.
        if (state === 'ok') window.setTimeout(() => dismissToast(id), SUCCESS_MS);
      };

      /*
        Deliberately not awaited by the caller: the dialog is already gone. The
        promise is owned here and both outcomes are handled, so nothing can
        surface as an unhandled rejection in the console instead of on screen.
      */
      void task
        .run()
        .then(async (result) => {
          settle('ok', task.done?.(result) ?? 'Done.');
          for (const key of task.invalidate ?? []) {
            await queryClient.invalidateQueries({ queryKey: key });
          }
        })
        .catch((err: unknown) => {
          settle('failed', err instanceof Error ? err.message : 'It did not finish.');
        });
    },
    [dismissToast, queryClient],
  );

  const value = useMemo<TasksApi>(
    () => ({
      start,
      tasks,
      running: tasks.filter((t) => t.state === 'running').length,
      dismissToast,
      clearFinished,
    }),
    [start, tasks, dismissToast, clearFinished],
  );

  return <TasksContext.Provider value={value}>{children}</TasksContext.Provider>;
}

export function useTasks(): TasksApi {
  const ctx = useContext(TasksContext);
  if (!ctx) throw new Error('useTasks must be used inside a TasksProvider');
  return ctx;
}
